import React from 'react'
import { ThemeProvider, useTheme } from '../context/ThemeContext'

function TermsOfServiceContent() {
  const { isDark, toggleTheme } = useTheme()

  const sectionStyle = { marginBottom: '2.5rem' }

  const headingStyle = {
    fontFamily: 'Georgia, serif',
    fontSize: '1.05rem',
    fontWeight: '700',
    color: 'var(--text)',
    marginBottom: '0.75rem',
    marginTop: 0,
    paddingBottom: '0.5rem',
    borderBottom: '1px solid var(--border)',
  }

  const bodyStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '0.88rem',
    color: 'var(--text-muted)',
    lineHeight: '1.8',
    margin: 0,
  }

  const linkStyle = { color: '#c9a84c', textDecoration: 'none' }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 1.5rem', height: '58px',
        background: 'var(--bg)', borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <a href="/" style={{ fontFamily: 'Georgia, serif', fontSize: '1.15rem', fontWeight: '700', color: 'var(--text)', textDecoration: 'none' }}>
          DebtCrush
        </a>
        <button
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.44rem 0.6rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem', cursor: 'pointer', lineHeight: 1 }}
        >
          {isDark ? '☀' : '🌙'}
        </button>
      </header>

      {/* Content */}
      <main style={{ maxWidth: '760px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>

        {/* Page title */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
            Legal
          </div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '2rem', fontWeight: '700', color: 'var(--text)', margin: '0 0 0.5rem' }}>
            Terms of Service
          </h1>
          <p style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0 }}>
            Last updated: June 2026 &nbsp;·&nbsp; DebtCrush
          </p>
        </div>

        {/* Intro */}
        <div style={{ ...sectionStyle, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem', borderLeft: '3px solid #c9a84c' }}>
          <p style={{ ...bodyStyle, color: 'var(--text)' }}>
            Please read these Terms of Service carefully before using <strong>DebtCrush</strong> at{' '}
            <a href="https://debtcrush.in" style={linkStyle}>debtcrush.in</a>. By accessing or using our service, you agree to be bound by these terms. If you do not agree, please do not use DebtCrush.
          </p>
        </div>

        {/* 1. Acceptance of Terms */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Acceptance of Terms</h2>
          <p style={bodyStyle}>
            By creating an account or using any part of the DebtCrush platform, you confirm that you are at least 18 years of age, have read and understood these Terms, and agree to be legally bound by them. If you are using DebtCrush on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms.
          </p>
        </div>

        {/* 2. Description of Service */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Description of Service</h2>
          <p style={bodyStyle}>
            DebtCrush is a personal finance management tool that helps you track loans, EMI payments, insurance policies, expenses, investments, and net worth. The platform provides financial summaries, calculators, health scores, and suggestions for informational purposes only.
          </p>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            <strong style={{ color: 'var(--text)' }}>DebtCrush is not a financial advisor, bank, or lending institution.</strong> Nothing on this platform constitutes financial, legal, tax, or investment advice. Always consult a qualified professional before making financial decisions.
          </p>
        </div>

        {/* 3. User Accounts */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. User Accounts</h2>
          <p style={bodyStyle}>When you create an account, you agree to:</p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Provide accurate, complete, and current information during registration.</li>
            <li style={{ marginBottom: '0.5rem' }}>Maintain the security of your password and account credentials.</li>
            <li style={{ marginBottom: '0.5rem' }}>Notify us immediately at <a href="mailto:support@debtcrush.in" style={linkStyle}>support@debtcrush.in</a> if you suspect any unauthorised access to your account.</li>
            <li style={{ marginBottom: '0.5rem' }}>Take full responsibility for all activities that occur under your account.</li>
          </ul>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            We reserve the right to suspend or terminate accounts that violate these Terms or that show signs of fraudulent or abusive activity.
          </p>
        </div>

        {/* 4. Acceptable Use */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Acceptable Use</h2>
          <p style={bodyStyle}>You agree to use DebtCrush only for lawful purposes. You must not:</p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Use the service to engage in any illegal, fraudulent, or harmful activity.</li>
            <li style={{ marginBottom: '0.5rem' }}>Attempt to gain unauthorised access to any part of the platform, its servers, or databases.</li>
            <li style={{ marginBottom: '0.5rem' }}>Reverse-engineer, decompile, or disassemble any part of the service.</li>
            <li style={{ marginBottom: '0.5rem' }}>Scrape, crawl, or use automated tools to extract data from the platform.</li>
            <li style={{ marginBottom: '0.5rem' }}>Transmit viruses, malware, or any code intended to disrupt or damage the service.</li>
            <li style={{ marginBottom: '0.5rem' }}>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
            <li style={{ marginBottom: '0.5rem' }}>Use the service in a way that could overburden, damage, or impair its infrastructure.</li>
          </ul>
        </div>

        {/* 5. Your Data */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Your Data</h2>
          <p style={bodyStyle}>
            You retain full ownership of the financial data you enter into DebtCrush. By using the service, you grant us a limited, non-exclusive licence to store and process your data solely for the purpose of providing and improving the service.
          </p>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            You are responsible for the accuracy of the data you enter. DebtCrush is a tracking tool — figures and projections are based entirely on the information you provide. We are not liable for decisions made based on incorrect data. Please refer to our{' '}
            <a href="/privacy-policy" style={linkStyle}>Privacy Policy</a> for details on how we handle your data.
          </p>
        </div>

        {/* 6. Intellectual Property */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Intellectual Property</h2>
          <p style={bodyStyle}>
            All content, design, code, logos, and materials on the DebtCrush platform are the property of DebtCrush and are protected by applicable intellectual property laws. You may not copy, reproduce, distribute, or create derivative works from any part of the service without our prior written consent.
          </p>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            Feedback, suggestions, or ideas you submit to us may be used by DebtCrush without any obligation to compensate you.
          </p>
        </div>

        {/* 7. Disclaimer of Warranties */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Disclaimer of Warranties</h2>
          <p style={bodyStyle}>
            DebtCrush is provided <strong style={{ color: 'var(--text)' }}>"as is"</strong> and <strong style={{ color: 'var(--text)' }}>"as available"</strong> without warranties of any kind, either express or implied. We do not warrant that:
          </p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>The service will be uninterrupted, error-free, or secure at all times.</li>
            <li style={{ marginBottom: '0.5rem' }}>Any calculations, projections, or suggestions are accurate or suitable for your specific financial situation.</li>
            <li style={{ marginBottom: '0.5rem' }}>The service will meet your particular requirements or expectations.</li>
          </ul>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            Your use of the service is entirely at your own risk.
          </p>
        </div>

        {/* 8. Limitation of Liability */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Limitation of Liability</h2>
          <p style={bodyStyle}>
            To the fullest extent permitted by applicable law, DebtCrush and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, or financial loss — arising from your use of or inability to use the service, even if we have been advised of the possibility of such damages.
          </p>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            Our total liability to you for any claims arising from your use of the service shall not exceed the amount you paid us in the twelve months preceding the claim (or ₹500, whichever is greater).
          </p>
        </div>

        {/* 9. Termination */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Termination</h2>
          <p style={bodyStyle}>
            You may stop using DebtCrush and delete your account at any time from your Profile settings. We reserve the right to suspend or terminate your access to the service at our discretion, without notice, if we believe you have violated these Terms or engaged in conduct harmful to other users or the platform.
          </p>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            Upon termination, your right to use the service ceases immediately. Provisions of these Terms that by their nature should survive termination (including ownership, disclaimers, and limitations of liability) will remain in effect.
          </p>
        </div>

        {/* 10. Changes to Terms */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Changes to These Terms</h2>
          <p style={bodyStyle}>
            We may update these Terms of Service from time to time. When we make material changes, we will notify you via an in-app notification or by email. The updated Terms will be available at{' '}
            <a href="https://debtcrush.in/terms-of-service" style={linkStyle}>debtcrush.in/terms-of-service</a>{' '}
            with a revised "Last updated" date. Continued use of DebtCrush after changes are posted constitutes your acceptance of the revised Terms.
          </p>
        </div>

        {/* 11. Governing Law */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Governing Law</h2>
          <p style={bodyStyle}>
            These Terms are governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of DebtCrush shall be subject to the exclusive jurisdiction of the courts located in India. If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
          </p>
        </div>

        {/* 12. Contact Us */}
        <div style={{ ...sectionStyle, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.5rem' }}>
          <h2 style={{ ...headingStyle, borderBottom: 'none', paddingBottom: 0 }}>12. Contact Us</h2>
          <p style={{ ...bodyStyle, marginTop: '0.5rem' }}>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: '60px' }}>Email</span>
              <a href="mailto:support@debtcrush.in" style={{ ...linkStyle, fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: '600' }}>support@debtcrush.in</a>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: '60px' }}>Website</span>
              <a href="https://debtcrush.in" style={{ ...linkStyle, fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: '600' }}>debtcrush.in</a>
            </div>
          </div>
        </div>

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <a
            href="/"
            style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.08em' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            ← Back to DebtCrush
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--border)', padding: '1.5rem', textAlign: 'center' }}>
        <p style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', margin: 0 }}>
          © {new Date().getFullYear()} DebtCrush · All rights reserved
        </p>
      </footer>
    </div>
  )
}

export default function TermsOfService() {
  return (
    <ThemeProvider>
      <TermsOfServiceContent />
    </ThemeProvider>
  )
}
